import cloneDeep from 'lodash/cloneDeep';
import { getLoadingMessage, tryParseJSONObject } from '../../shared/utils';

const formatResponses = (responses, header) => {
  const oldAddressFieldToLabelMap = {
    'city/province': 'City/Province',
    addressLine1: 'Address Line 1',
    addressLine2: 'Address Line 2',
    postalCode: 'Postal Code',
    country: 'Country',
    state: 'State'
  };

  const resolveAddressValue = (address) => {
    if (!address) return address;

    let parsedAddress = address;
    const newLines = [];

    // BACKWARD COMPATIBILITY: Parse previously stringified address response.
    if (typeof address === 'string' && tryParseJSONObject(address)) {
      parsedAddress = JSON.parse(address);
    }

    Object.entries(parsedAddress).forEach(([field, fieldData]) => {
      // BACKWARD COMPATIBILITY: Pick up field label for old response object.
      let label = oldAddressFieldToLabelMap?.[field] ?? field;
      let value = fieldData || '---';

      if (typeof value === 'object') {
        value = fieldData.value ?? '---';
        label = fieldData.label;
      }
      
      newLines.push(`${label}: ${value}`);
    });

    return newLines.join('<br>');
  };

  const resolveMatrixValue = (matrixValue) => {
    const valueLines = matrixValue.split('<br>');
    const questions = valueLines.map((line) => line.split(': ')[0]);
    const answers = valueLines.map((line) => {
      const splitValue = line.split(': ')[1];
      // Sometimes `splitValue` comes as 'undefined' of type string
      // and sometimes it comes as undefined of type undefined
      const isNoValue = splitValue === 'undefined' || !splitValue;
      return isNoValue ? '---' : splitValue.trim();
    });

    const newValue = questions.map((question, index) => {
      return `${question}: ${answers[index]}`;
    });

    return newValue.join('<br>');
  };

  for (let i = 0; i < responses.length; i += 1) {
    const response = responses[i];
    const responseKeys = Object.keys(response);

    responseKeys.forEach((key) => {
      if (header && typeof header === 'object') {
        const fieldType = header.find(({ field }) => field === key)?.type;

        if (fieldType === 'address' || (fieldType === 'lookup' && response[key].includes('address'))) {
          response[key] = resolveAddressValue(response[key]);
        }

        if (fieldType === 'matrix_rating' && response[key]) {
          response[key] = resolveMatrixValue(response[key]);
        }
      }
    });
  }

  return responses;
};

const defaultState = {
  error: '',
  loading: true,
  formDefinitionLoading: true,
  message: getLoadingMessage(),
  actionMessage: '',
  useDatastore: false,
  form: {},
  formName: '',
  responses: [],
  header: [],
  fields: [],
  multipleChoiceFields: [
    'select_basic',
    'select_multiple',
    'checkboxes',
    'radio_buttons',
    'star_rating',
    'heart_rating',
    'scale_rating',
    'matrix_rating',
    'smiley_rating',
    'image_choice',
  ],
  submissionsCount: 0,
  totalResponseCount: 0,
  versions: [],
  records: [],
  majorVersion: -1,
  latestVersion: 0,
  chartColorGroups: [
    ['#C5D86D', '#F46036', '#2E294E', '#1B998B', '#E71D36'],
    ['#003E1F', '#D96C06', '#BDBF09', '#2292A4', '#48304D'],
    ['#73BA9B', '#BA2D0B', '#FAA916', '#3F6634', '#52FFEE'],
    ['#1FB7A3', '#032B43', '#3F88C5', '#FFBA08', '#D00000'],
    ['#F9DC5C', '#BCEDF6', '#ED254E', '#4C2A85', '#59C3C3'],
    ['#5F00BA', '#43BCCD', '#EA3546', '#173753', '#F9C80E'],
    ['#264653', '#2A9D8F', '#E9C46A', '#F4A261', '#E76F51'],
    ['#FD9A30', '#7EFD39', '#6F2DBD', '#FFE66D'],
    ['#FF6B6B', '#4ECDC4', '#DC493A', '#FC2BC5'],
    ['#2667FF', '#03A777', '#820263', '#D90568'],
    ['#F65C04', '#ABFF4F', '#FF9915', '#0ABDBD'],
    ['#29BF12', '#DA222A', '#1F7A8C', '#063B5E'],
    ['#003E1F', '#73BA9B', '#A31722', '#ED217C'],
    ['#C3423F', '#9BC53D', '#FDE74C', '#5BC0EB'],
    ['#69124A', '#080B39', '#14527C', '#F248B6'],
  ],
  charts: [
    'Frequency Table',
    'Bar Chart',
    'Horizontal Bar Chart',
    'Pie Chart',
    'Donut Chart',
    'Gauge Chart',
    'Stacked Bar Chart',
    'Grouped Bar Chart',
    'Figure',
    'Word Cloud',
  ],
  plotlyChart: {
    data: [],
    layout: {
      title: 'FormPlus Plot',
      autosize: true,
      font: {
        family: 'Quicksand',
      },
    },
    config: {
      toImageButtonOptions: {
        format: 'png',
        filename: 'FormPlusCharts',
        height: 500,
        width: 700,
        scale: 1,
      },
      autosizable: true,
      displaylogo: false,
      displayModeBar: true,
      responsive: true,
      modeBarButtonsToRemove: [
        'sendDataToCloud',
        'zoom2d',
        'pan2d',
        'select2d',
        'lasso2d',
        'toImage',
        'resetScale2d',
        'toggleSpikelines',
        'hoverClosestCartesian',
        'hoverCompareCartesian',
        'toggleHover',
        'hoverClosestPie',
      ],
    },
  },
  reports: [],
  report: {
    reportTitle: '',
    reportDescription: '',
    reportID: 0,
    chartsInfo: [],
    shareReport: false,
    default: false,
    allFieldsFilters: {},
  },
  prevCursorString: '',
  nextCursorString: '',
  pageSize: 50,
  location: [],
  useSheets: false,
  spreadsheetKey: '',
  responseFilter: {
    id: 0,
    title: '',
    roles: [],
    default: false,
    filterable: true,
    filter: {
      hidden_columns: [],
      sort: [],
      search: [],
      advance_filters: [],
    },
  },
  allFilters: [],
  filterValue: 0,
  conditionLabels: {
    empty: 'is empty',
    not_empty: 'is not empty',
    equal: 'equals',
    not_equal: "doesn't equal",
    contain: 'contains',
    not_contain: 'does not contain',
    starts_with: 'starts with',
    ends_with: 'ends with',
    greater_than: 'greater than',
    greater_than_equal: 'greater than or equal to',
    less_than: 'less than',
    less_than_equal: 'less than or equal to',
  },
  filterCondition: {
    empty: "(row['id'] === '')",
    not_empty: "(!(row['id'] === ''))",
    equal: "(row['id'] === 'value')",
    not_equal: "(row['id'] !== 'value')",
    contain: "(row['id'].includes('value'))",
    not_contain: "(!(row['id'].includes('value')))",
    starts_with: "(row['id'].startsWith('value'))",
    ends_with: "(row['id'].endsWith('value'))",
    greater_than: "(parseInt(row['id']) > parseInt('value'))",
    greater_than_equal: "(parseInt(row['id']) >= parseInt('value'))",
    less_than: "(parseInt(row['id']) < parseInt('value'))",
    less_than_equal: "(parseInt(row['id']) <= parseInt('value'))",
  },
  reportLink: '',
  refresh: false,
  savedResponses: [],
  savedRespsHeaders: [],
  savedRespsPageSize: 50,
  savedRespsPrevCursor: '',
  savedRespsNextCursor: '',
  savedRespCount: 0,
  filtersCount: 0,
  loadingSavedResponses: false,
  downloadCSV: false,
  isPositive: false,
  editedFilter: false,
  editedReport: false,
};

export default function responsesReducers(state = defaultState, action) {
  const { type, payload } = action;
  switch (type) {
    case 'FETCH_NOTES_SUCCESS':
    case 'DELETE_NOTE_SUCCESS':
    case 'UPDATE_NOTE_SUCCESS': {
      const editableState = cloneDeep(state);
      for (let i = 0; i < editableState.responses.length; i++) {
        if (editableState.responses[i]['4'] === payload.result_row) {
          editableState.responses[i]['6'] = payload.notes;
        }
      }
      return { ...state, ...editableState };
    }

    case 'FETCH_FORM_DEFINITION_START':
    case 'FETCH_FORM_DEFINITION_FAILURE': {
      return { ...state, ...payload }
    }

    case 'FETCH_FORM_DEFINITION_SUCCESS': {
      const {
        options: { enable_esignature },
        unallowed_premium_fields,
        definition,
      } = payload.form;
      
      return {
        ...state,
        unallowedPremiumFields: unallowed_premium_fields,
        formDefinitionLoading: false,
        form: {
          ...cloneDeep(definition),
          enable_esignature
        }
      };
    }

    case 'UPGRADE_PLAN_SUCCESS': {
      window.PLAN = payload.plan;
      return { ...state, ...payload };
    }

    case 'FETCH_FORM_RESPONSES': {
      if (payload.error) return { ...state, ...payload };

      if (!payload.spreadsheetKey) {
        payload.spreadsheetKey = '';
      }

      let filterables = payload.allFilters.filter((filter) => filter.filterable);
      filterables = filterables.map((filter) => ({
        idx: filter.id,
        filterable: filter.filterable,
        modified: filter.title,
      }));

      const nonFilterables = payload.allFilters.filter((filter) => !filter.filterable);

      // Ensure that All submission is first in nonfilterables
      let orderedNonFilterables = [];
      let orderedVersions = [];
      for (const idx in nonFilterables) {
        const nonFilterable = nonFilterables[idx];
        if (nonFilterable.title === 'All Submissions') {
          orderedNonFilterables.unshift({
            idx: nonFilterable.id,
            filterable: nonFilterable.filterable,
            modified: nonFilterable.title,
          });
        } else {
          orderedVersions.push({
            idx: nonFilterable.id,
            filterable: nonFilterable.filterable,
            modified: nonFilterable.title,
            version: nonFilterable.version,
          });
        }
      }

      // Ensure that versions are in most recent order
      orderedVersions.sort((a, b) => b.version - a.version);
      orderedVersions = orderedVersions.map((filter) => ({
        idx: filter.idx,
        filterable: filter.filterable,
        modified: filter.modified,
      }));
      orderedNonFilterables = [...orderedNonFilterables, ...orderedVersions];

      const versions = filterables.concat(orderedNonFilterables);

      const editableState = cloneDeep(state);
      let { filterValue } = editableState;
      if (payload.responseFilter) {
        filterValue = payload.responseFilter.id;
      } else {
        const defaultFilter = payload.allFilters.filter((filter) => filter.default);
        if (defaultFilter.length !== 0) {
          payload.responseFilter = {
            id: defaultFilter[0].id,
            title: defaultFilter[0].title,
            roles: [],
            default: defaultFilter[0].default,
            filter: {},
            downloadCount: defaultFilter[0].download_count,
          };
          filterValue = payload.responseFilter.id;
        }
      }

      payload.responses = formatResponses(payload.responses, payload.header);
      return { ...state, ...payload, versions, filterValue };
    }

    case 'FETCH_RESPONSES_ANALYTICS_SUCCESS': {
      return { ...state, ...payload };
    }

    case 'FETCH_RESPONSES_ANALYTICS_FAILURE': {
      return { ...state, ...payload };
    }

    case 'FETCH_RESPONSES_REPORTS': {
      return { ...state, ...payload };
    }

    case 'SAVE_RESPONSES_REPORTS': {
      const editableState = cloneDeep(state);
      let { reportLink } = editableState;
      const { reports } = editableState;
      let { report } = editableState;
      if (payload.chartsData) {
        reportLink =
          payload.report_id === 0 ? '' : `${window.location.origin}/e/${payload.formId}/report/${payload.report_id}`;
        reports.forEach((report, idx) => {
          if (report.report_id === payload.report_id) {
            reports.splice(idx, 1);
          }
        });
        reports.unshift({
          charts_info: JSON.stringify(payload.chartsData.chartsInfo),
          default: payload.chartsData.default,
          description: payload.chartsData.description,
          report_id: payload.report_id,
          share_report: payload.chartsData.shareReport,
          title: payload.chartsData.title,
          allFieldsFilters: payload.chartsData.allFieldsFilters,
        });
        report = {
          chartsInfo: payload.chartsData.chartsInfo,
          default: payload.chartsData.default,
          reportDescription: payload.chartsData.description,
          reportID: payload.report_id,
          shareReport: payload.chartsData.shareReport,
          reportTitle: payload.chartsData.title,
          allFieldsFilters: payload.chartsData.allFieldsFilters,
        };
      }
      return { ...state, ...payload, reportLink, reports, report };
    }

    case 'DELETE_FORM_RESPONSES': {
      return { ...state, ...payload };
    }

    case 'FETCH_FORM_LOCATION_SUCCESS': {
      return { ...state, ...payload };
    }

    case 'FETCH_FORM_LOCATION_FAILURE': {
      return { ...state, ...payload };
    }

    case 'SAVE_FILTER_START': {
      return { ...state, ...payload };
    }

    case 'SAVE_FILTER_SUCCESS': {
      const editableState = cloneDeep(state);
      let { versions } = editableState;
      const filterValue = payload.filter.id;
      const responseFilter = payload.filter;
      versions.forEach((version, idx) => {
        if (version.idx === payload.filter.id) {
          versions.splice(idx, 1);
        }
      });
      versions = [
        ...editableState.versions,
        {
          idx: payload.filter.id,
          filterable: payload.filter.filterable,
          modified: payload.filter.title,
        },
      ];
      return { ...state, versions, filterValue, responseFilter };
    }

    case 'SAVE_FILTER_FAILURE': {
      return { ...state, ...payload };
    }

    case 'DELETE_FILTER_START': {
      return { ...state, ...payload };
    }

    case 'DELETE_FILTER_SUCCESS': {
      return { ...state, ...payload };
    }

    case 'DELETE_FILTER_FAILURE': {
      return { ...state, ...payload };
    }

    case 'DELETE_REPORTS_SUCCESS': {
      return { ...state, ...payload };
    }

    case 'DELETE_REPORTS_FAILURE': {
      return { ...state, ...payload };
    }

    case 'REFRESH_FORM_RESPONSES': {
      return { ...state, ...payload };
    }

    case 'NOTIFY_SHOW': {
      return { ...state, ...payload };
    }

    case 'NOTIFY_HIDE': {
      return { ...state, ...payload };
    }

    case 'DOWNLOAD_RESPONSES_CSV_START': {
      return { ...state, ...payload };
    }

    case 'DOWNLOAD_RESPONSES_CSV_SUCCESS': {
      return { ...state, ...payload };
    }

    case 'DOWNLOAD_RESPONSES_CSV_FAILURE': {
      return { ...state, ...payload };
    }

    case 'FETCH_SAVED_RESPONSES_START': {
      return { ...state, ...payload };
    }

    case 'FETCH_SAVED_RESPONSES_SUCCESS': {
      payload.savedResponses = formatResponses(payload.savedResponses, payload.savedRespsHeaders);
      const fieldToRemove = ['2', '3', '5'];
      if (payload.savedRespsHeaders) {
        payload.savedRespsHeaders = payload.savedRespsHeaders.filter(
          (header) => fieldToRemove.indexOf(header.field) < 0
        );
        payload.savedRespsHeaders.forEach((header) => {
          if (header.field === '4') {
            header.title = 'Entry ID';
          }
        });
      }
      return { ...state, ...payload };
    }

    case 'SAVE_USER_OPTIONS_START': {
      return { ...state, ...payload };
    }

    case 'EDIT_FILTER': {
      return { ...state, ...payload };
    }

    case 'EDIT_REPORT': {
      return { ...state, ...payload };
    }

    case 'EDIT_REPORT_DONE': {
      return { ...state, ...payload };
    }

    default: {
      return state;
    }
  }
}
